import type { FilterFieldConfig } from '@pesto/ui';

import {
  createBooleanColumn,
  createColumnConfig,
  createDateColumn,
  createFilterField,
  createSortableColumnId,
  createTextColumn,
  useGenericListConfig,
} from '@pesto/ui';
import { DataTableColumnHeader } from '@pesto/ui/components/DataTable/DataTableColumnHeader';
import { generateBooleanFilterOptions, generateEnumFilterOptions } from '@pesto/ui/features/SmartTable/utils/helpers';
import {
  centsToFormattedDollars,
  formatBorrower,
  formatLoanStatus,
  formatRate,
  LOAN_STATUS_LABELS,
} from '@pesto/utils';

import type { LoanListItem } from '../toLoanListItem';

import { LoanStatus } from '../../../__generated__/graphql/api';

const columnIds = {
  loanId: createSortableColumnId('idSearch', 'idSortDirection'),
  partnerAccountNumber: createSortableColumnId('partnerAccountNumberSearch', 'partnerAccountNumberSortDirection'),
  hasPromo: 'hasPromo',
  status: createSortableColumnId('statusSelections', 'statusSortDirection'),
  creditLimit: 'creditLimit',
  aprMargin: 'aprMargin',
  delinquent: 'isDelinquent',
  program: 'program',
  borrower: createSortableColumnId('', 'borrowerSortDirection'),
  propertyState: 'propertyState',
  originationDate: createSortableColumnId('', 'originationDateSortDirection'),
  dateOfLastActivity: createSortableColumnId('', 'dateOfLastActivitySortDirection'),
  closedAt: createSortableColumnId('', 'closedAtSortDirection'),
};

export const useLoansListConfig = (programFilterFields: FilterFieldConfig[]) => {
  const filterFields = [
    createFilterField('Loan ID', columnIds.loanId, { placeholder: 'Loan ID search' }),
    createFilterField('Partner Account #', columnIds.partnerAccountNumber, { placeholder: 'Partner account #' }),
    createFilterField('Promo', columnIds.hasPromo, {
      options: generateBooleanFilterOptions({ trueLabel: 'Has promo', falseLabel: 'No promo', allLabel: 'Any promo' }),
      variant: 'boolean',
    }),
    createFilterField('Status', columnIds.status, {
      options: generateEnumFilterOptions(LoanStatus, LOAN_STATUS_LABELS, false),
    }),
    ...programFilterFields,
  ];

  const columns = [
    createColumnConfig<LoanListItem>(
      columnIds.loanId,
      ({ column }) => <DataTableColumnHeader column={column} title="Loan ID" />,
      {
        accessorFn: (row) => row.id,
        cellType: 'text',
      },
    ),
    createTextColumn<LoanListItem>(
      columnIds.partnerAccountNumber,
      'Partner Account #',
      (row) => row.partnerAccountNumber,
    ),
    createColumnConfig<LoanListItem>(
      columnIds.status,
      ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      {
        accessorFn: (row) => formatLoanStatus(row.status),
        cellType: 'text',
      },
    ),
    createColumnConfig<LoanListItem>(columnIds.creditLimit, 'Credit Limit', {
      accessorFn: (row) => centsToFormattedDollars(row.creditLimit),
      cellType: 'text',
    }),
    createTextColumn<LoanListItem>(columnIds.aprMargin, 'APR Margin', (row) => formatRate(row.aprMargin) ?? '—'),
    createBooleanColumn<LoanListItem>(columnIds.delinquent, 'Delinquent', (row) => row.isDelinquent),
    createTextColumn<LoanListItem>(columnIds.program, 'Program', (row) => row.program?.name),
    createColumnConfig<LoanListItem>(
      columnIds.borrower,
      ({ column }) => <DataTableColumnHeader column={column} title="Borrower" />,
      {
        accessorFn: (row) => formatBorrower(row.users),
        cellType: 'text',
      },
    ),
    createTextColumn<LoanListItem>(columnIds.propertyState, 'Property State', (row) => row.propertyState ?? '—'),
    createDateColumn<LoanListItem>(
      columnIds.originationDate,
      ({ column }) => <DataTableColumnHeader column={column} title="Date Open" />,
      (row) => row.originationDate,
      { dateFormat: 'd MMM yyyy' },
    ),
    createDateColumn<LoanListItem>(
      columnIds.dateOfLastActivity,
      ({ column }) => <DataTableColumnHeader column={column} title="Date of Last Activity" />,
      (row) => row.dateOfLastActivity,
      { dateFormat: 'd MMM yyyy' },
    ),
    createDateColumn<LoanListItem>(
      columnIds.closedAt,
      ({ column }) => <DataTableColumnHeader column={column} title="Closed" />,
      (row) => row.closedAt,
      { dateFormat: 'd MMM yyyy' },
    ),
  ];

  return useGenericListConfig<LoanListItem>({
    columns,
    filterFields,
    showActionColumn: true,
    actionButtonText: 'View',
    pinnedColumns: ['view'],
  });
};
